import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Drag to reorder the handbook's pages in its contents (playbook/_shell), for
// the admins who edit it. A page drags only within its own list - one section
// of one part - and the list's new order is saved in one PATCH
// (PlaybookController#reorder), which renumbers the whole handbook. Nothing
// redraws: the row is already where it was dropped. Alt + Up / Down does the
// same from the keyboard, on a focused page link.
//
// The calendar drawer's pattern (calendar_drawer_reorder_controller), for
// <li> rows whose link must not open after a drag.
export default class extends Controller {
    static targets = ["item"]
    static values = { url: String }

    connect() {
        this.itemTargets.forEach((item) => this._enableKeys(item))
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return

        this._onDragOver = this._onDragOver.bind(this)
        this.element.addEventListener("dragover", this._onDragOver)
        this.element.addEventListener("drop", (e) => { if (this._dragging) e.preventDefault() })
        this.itemTargets.forEach((item) => this._enableDrag(item))
    }

    disconnect() {
        if (this._onDragOver) this.element.removeEventListener("dragover", this._onDragOver)
    }

    _enableDrag(item) {
        item.setAttribute("draggable", "true")
        item.addEventListener("dragstart", (e) => {
            this._dragging = item
            this._moved = false
            item.classList.add("is-dragging")
            e.dataTransfer.effectAllowed = "move"
            try { e.dataTransfer.setData("text/plain", item.dataset.pageId) } catch { /* ignore */ }
        })
        item.addEventListener("dragend", () => {
            item.classList.remove("is-dragging")
            this._dragging = null
            if (this._moved) {
                // Some browsers click after a drag; that click would open the page.
                this._suppressUntil = Date.now() + 300
                this._save(item.parentElement)
            }
        })
        item.addEventListener("click", (e) => {
            if (this._suppressUntil && Date.now() < this._suppressUntil) {
                e.preventDefault()
                e.stopPropagation()
            }
        }, true)
    }

    _enableKeys(item) {
        item.addEventListener("keydown", (e) => {
            if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return
            // Steps over rows that are not pages (Our team, under the first page).
            const step = e.key === "ArrowUp" ? "previousElementSibling" : "nextElementSibling"
            let sibling = item[step]
            while (sibling && !sibling.matches("[data-playbook-reorder-target~='item']")) sibling = sibling[step]
            if (!sibling) return

            e.preventDefault()
            item.parentElement.insertBefore(item, e.key === "ArrowUp" ? sibling : sibling.nextSibling)
            const link = item.querySelector("a")
            if (link) link.focus()
            this._save(item.parentElement)
        })
    }

    _onDragOver(e) {
        if (!this._dragging) return
        const over = e.target.closest ? e.target.closest("[data-playbook-reorder-target~='item']") : null
        if (!over || over === this._dragging || over.parentElement !== this._dragging.parentElement) return

        e.preventDefault()
        const rect = over.getBoundingClientRect()
        const before = (e.clientY - rect.top) < rect.height / 2
        over.parentElement.insertBefore(this._dragging, before ? over : over.nextSibling)
        this._moved = true
    }

    _save(list) {
        if (!this.urlValue || !list) return
        const ids = Array.from(list.querySelectorAll(":scope > [data-playbook-reorder-target~='item']")).map((item) => item.dataset.pageId)
        fetch(this.urlValue, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), Accept: "application/json" },
            body: JSON.stringify({ ids }),
            credentials: "same-origin"
        }).then((response) => {
            // A list that no longer matches the server (someone else moved a
            // page) reloads to the saved order rather than lying about it.
            if (!response.ok) window.location.reload()
        }).catch(() => { /* the next drag or a reload shows the saved order */ })
    }
}
